import React from 'react'
import { Box, Paper, Tooltip, Typography } from '@mui/material'

export interface HeroStatProps {
  /** Small uppercase label above the number. */
  label: string
  /**
   * The formatted value, ALREADY a string. Formatting is the caller's job:
   * a percentage, a score and a count are three different rounding decisions
   * and this component has no way to tell them apart. `null` renders the
   * em-dash placeholder and switches the caption to its empty form.
   */
  value: string | null
  /** Small grey line under the number, e.g. "of comments". */
  caption?: string
  /** Caption used when `value` is null. Defaults to "no data". */
  emptyCaption?: string
  /** Rich hover content. Rendering nothing here is what disables the tooltip. */
  tooltip?: React.ReactNode
  /** Accent colour for the number. Defaults to the dashboard's indigo. */
  accent?: string
  'data-testid'?: string
  valueTestId?: string
}

/** The dashboard's indigo. Extracted from the literal repeated ~12 times in the
 *  original page, so a re-brand is one edit rather than a search. */
export const HERO_ACCENT = '#667eea'

/**
 * One big headline number in the KPI strip.
 *
 * This is the primitive the original page hand-rolled THREE times — Sky
 * Relevant, Sky Attributed and Cultural Relevance were three near-identical
 * 40-line `<Paper>` blocks differing only in label, value expression and
 * whether they had a tooltip. That is exactly the mechanical work §5c says must
 * never be hard: adding a fourth headline stat is now one line, and a
 * department that wants a different one is not editing a layout.
 *
 * PRESENTATIONAL ONLY. It takes a formatted string, not a `PlatinumData`, not a
 * metric key and not a product. If this component ever needs to know which
 * department it is on, something has leaked out of the pages layer.
 *
 * The squared-off borders and zero radius are not a style choice to revisit —
 * the strip is edge-to-edge and the tiles butt against each other, so a radius
 * here shows as a gap.
 */
export const HeroStat: React.FC<HeroStatProps> = ({
  label,
  value,
  caption,
  emptyCaption = 'no data',
  tooltip,
  accent = HERO_ACCENT,
  'data-testid': testId,
  valueTestId,
}) => {
  const hasValue = value !== null && value !== undefined

  const tile = (
    <Paper
      elevation={0}
      data-testid={testId}
      sx={{
        flex: 1,
        p: 4,
        borderRadius: 0,
        borderRight: '1px solid',
        borderBottom: '1px solid',
        borderColor: 'rgba(0,0,0,0.06)',
        bgcolor: 'background.paper',
        textAlign: 'center',
        cursor: tooltip && hasValue ? 'help' : 'default',
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: 'grey.500', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}
      >
        {label}
      </Typography>
      <Typography
        data-testid={valueTestId}
        variant="h3"
        sx={{ fontWeight: 700, color: hasValue ? accent : 'grey.300', my: 1 }}
      >
        {hasValue ? value : '—'}
      </Typography>
      <Typography variant="body2" sx={{ color: 'grey.500' }}>
        {hasValue ? caption : emptyCaption}
      </Typography>
    </Paper>
  )

  // A tooltip with nothing to say is worse than none: it produces a hover
  // target that opens an empty box.
  if (!tooltip || !hasValue) return tile
  return (
    <Tooltip title={tooltip} arrow placement="top">
      {tile}
    </Tooltip>
  )
}

export interface StatStripProps {
  children: React.ReactNode
  'data-testid'?: string
}

/**
 * The edge-to-edge row the hero stats sit in.
 *
 * `gap: 0` with each tile carrying its own right border is how the original
 * strip achieved hairline separators that reach the full tile height; a flex
 * gap would leave the page background showing through instead.
 */
export const StatStrip: React.FC<StatStripProps> = ({ children, 'data-testid': testId }) => (
  <Box data-testid={testId} sx={{ display: 'flex', gap: 0 }}>
    {children}
  </Box>
)

export default HeroStat
